import { Check } from 'lucide-react';
import { useId } from 'react';
import {
  GALLERY_COLOR_SCHEME_OPTIONS,
  type GalleryColorScheme
} from '../lib/galleryCustomisation';

export function ColorSchemePicker({
  value,
  name,
  onChange,
  hint = 'Choose a paired palette for the gallery canvas, editorial bands, and controls.'
}: {
  value: GalleryColorScheme;
  name: string;
  onChange: (value: GalleryColorScheme) => void;
  hint?: string;
}) {
  const hintId = useId();

  return (
    <fieldset className="grid gap-2">
      <legend className="text-sm font-semibold text-ink">Colour scheme</legend>
      {hint ? <p id={hintId} className="text-sm text-ink/70">{hint}</p> : null}
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8"
        aria-describedby={hint ? hintId : undefined}
      >
        {GALLERY_COLOR_SCHEME_OPTIONS.map((option) => {
          const selected = value === option.id;
          return (
            <label key={option.id} className="relative min-w-0 cursor-pointer">
              <input
                type="radio"
                name={name}
                value={option.id}
                checked={selected}
                onChange={() => onChange(option.id)}
                className="peer sr-only"
                aria-label={option.name}
              />
              <span
                className={`grid h-full min-w-0 justify-items-center rounded-sm border bg-white/65 px-2 py-3 transition ${
                  selected
                    ? 'border-ink ring-1 ring-ink'
                    : 'border-ink/15 hover:border-ink/40'
                } peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black`}
              >
                <span
                  className="relative block h-16 w-16 shrink-0 overflow-hidden rounded-full border border-ink/10 bg-transparent shadow-sm"
                  data-colour-swatch={option.id}
                  aria-hidden="true"
                >
                  <span
                    className="absolute inset-x-0 top-0 h-1/2"
                    style={{ backgroundColor: option.backgroundColor }}
                    data-colour-swatch-half="background"
                  />
                  <span
                    className="absolute inset-x-0 bottom-0 h-1/2"
                    style={{ backgroundColor: option.accentColor }}
                    data-colour-swatch-half="accent"
                  />
                  {selected ? (
                    <span className="absolute inset-0 z-[1] m-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-ink shadow-sm ring-1 ring-ink/15">
                      <Check className="h-4 w-4" />
                    </span>
                  ) : null}
                </span>
                <span className={`pt-2 text-center text-[11px] leading-tight ${selected ? 'font-semibold text-ink' : 'text-ink/70'}`}>
                  {option.name}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
